import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
  Stack,
  Button,
  Collapse,
  Typography,
  CircularProgress,
  Divider,
  Card,
  CardContent,
  CardActions,
  Fab,
} from '@mui/material';
import { 
  Delete as DeleteIcon, 
  Add as AddIcon, 
  Article as ArticleIcon, 
  VideoCall as VideoCallIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material';
import { useProject } from '../contexts/ProjectContext';
import * as api from '../services/api';
import GenerateIdeasForm from './GenerateIdeasForm';
import { alpha } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useMediaQuery } from '@mui/material';

const POST_TYPES = {
  'article': 'Article'
};

const HIGHLIGHT_DURATION = 2000;

const IdeasTable = () => {
  const { selectedProject } = useProject();
  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const navigate = useNavigate();
  const [showGenerateForm, setShowGenerateForm] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const matches = useMediaQuery('(max-width:600px)');

  // Fetch ideas with pagination
  const fetchIdeas = useCallback(async (pageNum = 1) => {
    if (!selectedProject) return;
    
    setLoading(true);
    try {
      console.log('Fetching ideas for project:', selectedProject.id); // Debug log
      const response = await api.listIdeas(
        selectedProject.id, 
        selectedType || null, 
        pageNum,
        20
      );
      console.log('Received ideas:', response); // Debug log
      
      if (pageNum === 1) {
        setIdeas(response.items);
      } else {
        setIdeas(prev => [...prev, ...response.items]);
      }
      
      setTotalCount(response.total);
      setHasMore(response.items.length > 0 && ideas.length < response.total);
    } catch (error) {
      console.error('Error fetching ideas:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedProject, selectedType]);

  useEffect(() => {
    setPage(1);
    fetchIdeas(1);
  }, [fetchIdeas]);

  // Load more handler for infinite scroll
  const loadMore = useCallback(() => {
    if (!loading && hasMore) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchIdeas(nextPage);
    }
  }, [loading, hasMore, page, fetchIdeas]);

  // Filter ideas based on search
  const filteredIdeas = ideas.filter(idea =>
    idea.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (idea.description || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Group ideas by topic
  const groupedIdeas = React.useMemo(() => {
    const groups = {};
    filteredIdeas.forEach(idea => {
      const topic = idea.topic || 'Uncategorized';
      if (!groups[topic]) {
        groups[topic] = [];
      }
      groups[topic].push(idea);
    });
    return groups;
  }, [filteredIdeas]);

  // Delete handler - optimistic update
  const handleDelete = async (ideaId) => {
    try {
      // Optimistically remove the idea from the list
      setIdeas(prevIdeas => 
        prevIdeas.filter(idea => idea.id !== ideaId)
      );
      
      // Make API call
      await api.deleteIdea(ideaId);
    } catch (error) {
      console.error('Error deleting idea:', error);
      // If deletion fails, fetch ideas again to restore state
      fetchIdeas(1);
    }
  };

  const handleCreateContent = async (idea, postType) => {
    try {
      const response = await api.createFromIdea(idea.id, { post_type: postType });
      
      // Navigate to the appropriate content based on type
      if (response.content_type === 'article') {
        navigate(`/articles/${response.content_id}`);
      }
    } catch (error) {
      console.error('Error creating content from idea:', error);
      // You might want to add error handling UI here
    }
  };

  // Handle idea creation success
  const handleIdeaCreated = (newIdeas) => {
    setIdeas(prev => [...newIdeas, ...prev]);
  };

  return (
    <Box>
      {/* Top Actions Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack 
            direction={{ xs: 'column', sm: 'row' }} 
            spacing={2} 
            alignItems={{ xs: 'stretch', sm: 'center' }} 
            sx={{ mb: 2 }}
          >
            <Typography variant="h6" component="h2">
              Ideas Management
            </Typography>
            <Box sx={{ flex: { xs: 0, sm: 1 } }} />
            <Stack 
              direction={{ xs: 'column', sm: 'row' }} 
              spacing={1}
              width={{ xs: '100%', sm: 'auto' }}
            >
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setShowGenerateForm(prev => !prev)}
                color="primary"
                fullWidth={matches}
              >
                Generate New Ideas
              </Button>
              <Button
                variant="outlined"
                startIcon={<FilterIcon />}
                onClick={() => setShowFilters(prev => !prev)}
                fullWidth={matches}
              >
                Filters
              </Button>
            </Stack>
          </Stack>

          {/* Generate Form Section */}
          <Collapse in={showGenerateForm}>
            <Box sx={{ mt: 2, mb: showFilters ? 2 : 0 }}>
              <GenerateIdeasForm 
                onGenerate={handleIdeaCreated} 
                onClose={() => setShowGenerateForm(false)}
              />
            </Box>
          </Collapse>

          {/* Search and Filters Section */}
          <Collapse in={showFilters}>
            <Box sx={{ mt: 2 }}>
              <Stack direction="row" spacing={2}>
                <TextField
                  label="Search ideas"
                  variant="outlined"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  sx={{ flexGrow: 1 }}
                  InputProps={{
                    startAdornment: (
                      <SearchIcon color="action" sx={{ mr: 1 }} />
                    ),
                  }}
                />
                <FormControl sx={{ minWidth: 200 }}>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={selectedType}
                    label="Type"
                    onChange={(e) => {
                      setSelectedType(e.target.value);
                      setPage(1);
                      fetchIdeas(1);
                    }}
                  >
                    <MenuItem value="">All</MenuItem>
                    {Object.entries(POST_TYPES).map(([value, label]) => (
                      <MenuItem key={value} value={value}>{label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
            </Box>
          </Collapse>
        </CardContent>
      </Card>

      {/* Ideas Content */}
      {loading && page === 1 ? (
        <Box sx={{ textAlign: 'center', py: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Stack spacing={3}>
          {Object.entries(groupedIdeas).length === 0 ? (
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography color="text.secondary">
                No ideas found. Try generating some new ideas!
              </Typography>
            </Paper>
          ) : (
            <>
              {Object.entries(groupedIdeas).map(([topic, topicIdeas]) => (
                <Paper key={topic} sx={{ overflow: 'hidden' }}>
                  <Box
                    sx={{
                      px: 2,
                      py: 1,
                      bgcolor: 'primary.main',
                      color: 'primary.contrastText',
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight="medium">
                      {topic}
                    </Typography>
                  </Box>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Title</TableCell>
                          <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Type</TableCell>
                          <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Description</TableCell>
                          <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Created</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {topicIdeas.map((idea) => (
                          <TableRow key={idea.id}>
                            <TableCell>{idea.title}</TableCell>
                            <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                              {POST_TYPES[idea.post_type]}
                            </TableCell>
                            <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                              {idea.description || '-'}
                            </TableCell>
                            <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                              {new Date(idea.created_at).toLocaleDateString()}
                            </TableCell>
                            <TableCell align="right">
                              <Stack 
                                direction="row" 
                                spacing={1} 
                                justifyContent="flex-end"
                              >
                                <Tooltip title="Create Content">
                                  <IconButton 
                                    size="small"
                                    onClick={() => handleCreateContent(idea, 'article')}
                                  >
                                    <ArticleIcon />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete">
                                  <IconButton 
                                    size="small" 
                                    onClick={() => handleDelete(idea.id)}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Tooltip>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              ))}
              {hasMore && (
                <Box sx={{ textAlign: 'center', py: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={loadMore}
                    disabled={loading}
                    startIcon={loading && <CircularProgress size={16} />}
                  >
                    Load More
                  </Button>
                </Box>
              )}
            </>
          )}
        </Stack>
      )}

      {/* Floating Action Button for quick access */}
      <Fab
        color="primary"
        sx={{
          position: 'fixed',
          bottom: 16,
          right: 16,
          display: { xs: 'flex', sm: 'none' } // Only show on mobile
        }}
        onClick={() => setShowGenerateForm(true)}
      >
        <AddIcon />
      </Fab>
    </Box>
  );
};

export default IdeasTable;